// Renders a Card descriptor from the server.
//
// The card is DATA, not markup: the backend decides what a turn produced (an
// answer, a summary, a draft) and which sources it drew on, and this file only
// decides how that looks. Keeping the decision server-side is what stops the
// citation the user sees from disagreeing with the one that was recorded.

import type { Card, CardAction, CardSource } from "../types";

interface Props {
  card: Card;
  // The CARD is passed alongside the action, and it is load-bearing rather than
  // convenience: the draft a button acts on is `card.draftPreview` — the text the
  // user is looking at when they click. Reading a single mutable "latest draft"
  // instead means a button on a still-visible older card copies the wrong text, or
  // nothing at all.
  onAction: (action: CardAction, card: Card) => void;
  onOpen: (url: string) => void;
}

export function CardView({ card, onAction, onOpen }: Props) {
  return (
    <div className="card">
      {card.title && (
        <div className="card-head">
          <span className="card-icon" aria-hidden="true">
            {card.icon || "✦"}
          </span>
          <h2 className="card-title">{card.title}</h2>
        </div>
      )}

      {card.text && <div className="card-text" dangerouslySetInnerHTML={{ __html: card.text }} />}

      {card.facts && card.facts.length > 0 && (
        <dl className="facts">
          {card.facts.map(([k, v], i) => (
            <div key={i} style={{ display: "contents" }}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      )}

      {card.draftPreview && <div className="draft-preview">{card.draftPreview}</div>}

      {/* SOURCES AND CITATION ARE MUTUALLY EXCLUSIVE by construction on the server.
          Rendering both put "According to HR-115 (p. 1)" directly above a Sources
          block listing HR-115 — one source, announced twice, in two styles. */}
      {card.sources && card.sources.length > 0 ? (
        <div className="sources">
          <div className="sources-label">
            {card.sources.length === 1 ? "Source" : "Sources"}
          </div>
          {card.sources.map((s, i) => (
            <SourceRow key={i} source={s} onOpen={onOpen} />
          ))}
        </div>
      ) : (
        card.citation && (
          <div className="citation">
            📄 {card.citation.title}
            {card.citation.page ? ` (p. ${card.citation.page})` : ""}
          </div>
        )
      )}

      {card.actions && card.actions.length > 0 && (
        <div className="card-actions">
          {card.actions.map((a) => (
            <button key={a.id} type="button" className={`btn${a.primary ? " primary" : ""}`} onClick={() => onAction(a, card)}>
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** One openable document.
 *
 *  A button rather than an <a> because the handler decides how to open it, and
 *  because a link styled as a row invites a middle-click that would leave the app.
 *  The URL is still announced, so the target is not a mystery before clicking. */
function SourceRow({ source, onOpen }: { source: CardSource; onOpen: (url: string) => void }) {
  // AN UNLINKABLE ROW IS NOT A BUTTON. Rendered as a div rather than a disabled button,
  // because a disabled control still reads as "something you could click if you were
  // allowed to" and there is nothing to open.
  //
  // CHANGED 2026-09-11: this used to catch every FAQ row. The FAQ is linked now, because
  // the HR team is the only audience and they are the people who need to open it. The
  // branch is reached only when a row genuinely has no resolvable URL, which for an FAQ
  // row means its path could not be made absolute. Both kinds keep their own label, so a
  // reader can still tell the FAQ from a policy document.
  if (!source.url) {
    const isFaq = source.kind === "faq";
    const isUser = source.kind === "user";
    // A `user` row is ALWAYS unlinked, and unlike the other two that is not a fallback:
    // there is no document behind it. Somebody typed a sentence into this conversation
    // and the answer used it. The label is the entire purpose of the row — without it,
    // an answer resting on an unverified assertion looks identical to one resting on
    // Crown's policy of record.
    return (
      <div className="source-row is-unlinked">
        <span aria-hidden="true">{isUser ? "➕" : isFaq ? "💬" : "📄"}</span>
        <span className="doc">{source.title}</span>
        {source.page ? <span className="page">p. {source.page}</span> : null}
        <span className="source-kind">
          {isUser ? "Added here · not from Crown's documents" : isFaq ? "Internal FAQ · not policy" : "No link available"}
        </span>
      </div>
    );
  }
  const url = source.url;
  // The FAQ keeps its own icon and its label even when linked. The label is what stops a
  // reader treating internal working material as the policy of record, and that job does
  // not change because the row became openable.
  const isFaq = source.kind === "faq";
  return (
    <button type="button" className="source-row" onClick={() => onOpen(url)} title={url}>
      <span aria-hidden="true">{isFaq ? "💬" : "📄"}</span>
      <span className="doc">{source.title}</span>
      {source.page ? <span className="page">p. {source.page}</span> : null}
      {isFaq ? <span className="source-kind">Internal FAQ · not policy</span> : null}
      <span className="go" aria-hidden="true">
        ↗
      </span>
      <span className="sr-only">Open in a new tab</span>
    </button>
  );
}
